import { useQuery } from '@tanstack/react-query';
import { refineCustomerApi } from '../services/apiService';
import { logger } from '../utils/logger';

interface RefineCustomerDashboardData {
  surveyCount: number;
  responsesByType: {
    'مثبت': number;
    'منفی': number;
    'خنثی': number;
  };
  registeredReasons: {
    total: number;
    positive: number;
    negative: number;
  };
  topDissatisfactionReasons: Array<{
    reason: string;
    count: number;
  }>;
  dissatisfiedProducts: Array<{
    productName: string;
    reasonCount: number;
    reviewCount: number;
    wareCount: number;
    percentRank: number;
    dissatisfactionShare: number;
    ratePercent: number;
  }>;
  complaintStatus: {
    reviewed: number;
    notReviewed: number;
  };
  branchInfo: {
    branchId: number;
    branchName: string;
    lockNumber: string;
  };
}


export const useRefineCustomerDashboard = (branchId: number) => {
  return useQuery<RefineCustomerDashboardData>({
    queryKey: ['refine-customer-dashboard', branchId],
    queryFn: async () => {
      try {
        // Check if we're in development mode without backend
        const isDevelopment = process.env.NODE_ENV === 'development';
        
        try {
          const response = await refineCustomerApi.getDashboardData(branchId);
          
          // Log the response structure for debugging
          logger.debug('Refine customer dashboard API response', 'RefineCustomerQueries', response);
          
          // Handle different response structures
          if (response.data && response.data.data) {
            return response.data.data as RefineCustomerDashboardData;
          }
          
          // If data is directly in response.data
          if (response.data) {
            return response.data as RefineCustomerDashboardData;
          }
          
          // If response is the data itself
          if (response && typeof response === 'object') {
            return response as unknown as RefineCustomerDashboardData;
          }
        } catch (apiError) {
          // If API fails in development, return mock data
          if (isDevelopment) {
            logger.warn('API failed, returning mock data for development', 'RefineCustomerQueries', apiError);
            // return getMockData(branchId);
          }
          throw apiError;
        }
      } catch (error) {
        logger.error('Error fetching refine customer dashboard data', 'RefineCustomerQueries', error);
      }
      
      // Return empty data structure if no data or error
      return {
        surveyCount: 0,
        responsesByType: {
          'مثبت': 0,
          'منفی': 0,
          'خنثی': 0
        },
        registeredReasons: {
          total: 0,
          positive: 0,
          negative: 0
        },
        topDissatisfactionReasons: [],
        dissatisfiedProducts: [],
        complaintStatus: {
          reviewed: 0,
          notReviewed: 0
        },
        branchInfo: {
          branchId: branchId,
          branchName: '',
          lockNumber: ''
        }
      } as RefineCustomerDashboardData;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (garbage collection time)
    retry: 2,
    enabled: !!branchId,
  });
};

// Individual hooks for each section
export const useRefineCustomerSurveyCount = (branchId: number) => {
  return useQuery({
    queryKey: ['refine-customer-survey-count', branchId],
    queryFn: async () => {
      const response = await refineCustomerApi.getSurveyCount(branchId);
      return response.data?.totalSurveyForms || response.data || 0;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    enabled: !!branchId,
  });
};

export const useRefineCustomerResponsesByType = (branchId: number) => {
  return useQuery<RefineCustomerDashboardData['responsesByType']>({
    queryKey: ['refine-customer-responses-type', branchId],
    queryFn: async () => {
      const response = await refineCustomerApi.getResponsesByType(branchId);
      return response.data?.data || response.data || { 'مثبت': 0, 'منفی': 0, 'خنثی': 0 };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    enabled: !!branchId,
  });
};

export const useRefineCustomerRegisteredReasons = (branchId: number) => {
  return useQuery<RefineCustomerDashboardData['registeredReasons']>({
    queryKey: ['refine-customer-registered-reasons', branchId],
    queryFn: async () => {
      const response = await refineCustomerApi.getRegisteredReasons(branchId);
      return response.data?.data || response.data || { total: 0, positive: 0, negative: 0 };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    enabled: !!branchId,
  });
};

export const useRefineCustomerDissatisfactionReasons = (branchId: number, limit: number = 10) => {
  return useQuery<RefineCustomerDashboardData['topDissatisfactionReasons']>({
    queryKey: ['refine-customer-dissatisfaction-reasons', branchId, limit],
    queryFn: async () => {
      const response = await refineCustomerApi.getDissatisfactionReasons(branchId, limit);
      return response.data?.data || response.data || [];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    enabled: !!branchId,
  });
};

export const useRefineCustomerDissatisfiedProducts = (branchId: number) => {
  return useQuery<RefineCustomerDashboardData['dissatisfiedProducts']>({
    queryKey: ['refine-customer-dissatisfied-products', branchId],
    queryFn: async () => {
      const response = await refineCustomerApi.getDissatisfiedProducts(branchId);
      return response.data?.data || response.data || [];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    enabled: !!branchId,
  });
};

export const useRefineCustomerComplaintStatus = (branchId: number) => {
  return useQuery<RefineCustomerDashboardData['complaintStatus']>({
    queryKey: ['refine-customer-complaint-status', branchId],
    queryFn: async () => {
      const response = await refineCustomerApi.getComplaintStatus(branchId);
      return response.data?.data || response.data || { reviewed: 0, notReviewed: 0 };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    enabled: !!branchId,
  });
};

export const useRefineCustomerSurveyResponsesByAnswer = (branchId: number) => {
  return useQuery<Array<{ AnswerText: string; count: number }>>({
    queryKey: ['refine-customer-survey-responses-by-answer', branchId],
    queryFn: async () => {
      const response = await refineCustomerApi.getSurveyResponsesByAnswer(branchId);
      return response.data || [];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    enabled: !!branchId,
  });
};

export const useRefineCustomerDissatisfactionDetails = (branchId: number, categoryName: string) => {
  return useQuery<Array<{ reason: string; count: number }>>({
    queryKey: ['refine-customer-dissatisfaction-details', branchId, categoryName],
    queryFn: async () => {
      const response = await refineCustomerApi.getDissatisfactionDetails(branchId, categoryName);
      return response.data?.data || response.data || [];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    enabled: !!branchId && !!categoryName,
  });
};

export const useRefineCustomerTotalResponses = (branchId: number) => {
  return useQuery<number>({
    queryKey: ['refine-customer-total-responses', branchId],
    queryFn: async () => {
      const response = await refineCustomerApi.getTotalResponses(branchId);
      return response.data?.totalResponses || response.data?.data?.totalResponses || 0;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    enabled: !!branchId,
  });
};